import { ChartProps, DataRecord, getMetricLabel } from '@superset-ui/core';
import {
  CalendarChartFormData,
  CalendarChartTransformedProps,
  CalendarDayCell,
  CalendarLine,
  CalendarMonth,
  CalendarDisplayMode,
  CalendarOverflowMode,
  CalendarTextAlign,
  CalendarVerticalAlign,
} from './types';

function normalizeToUtcDate(value: unknown): Date | null {
  if (value === null || value === undefined || value === '') {
    return null;
  }

  const d = value instanceof Date ? value : new Date(value as any);
  if (Number.isNaN(d.getTime())) {
    return null;
  }

  return new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate()));
}

function monthStartUtc(date: Date): Date {
  return new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), 1));
}

function addDaysUtc(date: Date, days: number): Date {
  const d = new Date(date.getTime());
  d.setUTCDate(d.getUTCDate() + days);
  return new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate()));
}

function toMonthKey(date: Date): string {
  return `${date.getUTCFullYear()}-${String(date.getUTCMonth() + 1).padStart(2, '0')}`;
}

function toDayKey(date: Date): string {
  return `${date.getUTCFullYear()}-${String(date.getUTCMonth() + 1).padStart(2, '0')}-${String(
    date.getUTCDate(),
  ).padStart(2, '0')}`;
}

function daysInMonthUtc(date: Date): number {
  return new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth() + 1, 0)).getUTCDate();
}

function buildMonthGrid(
  monthStart: Date,
  linesByDay: Map<string, CalendarLine[]>,
): CalendarMonth {
  const firstDay = monthStartUtc(monthStart);
  const jsWeekDay = firstDay.getUTCDay();
  const mondayOffset = (jsWeekDay + 6) % 7;
  const monthDays = daysInMonthUtc(firstDay);

  const totalSlots = mondayOffset + monthDays;
  const weeksCount = Math.ceil(totalSlots / 7);
  const cellCount = weeksCount * 7;
  const gridStart = addDaysUtc(firstDay, -mondayOffset);

  const cells: CalendarDayCell[] = [];

  for (let i = 0; i < cellCount; i += 1) {
    const date = addDaysUtc(gridStart, i);
    const key = toDayKey(date);
    const inCurrentMonth =
      date.getUTCFullYear() === monthStart.getUTCFullYear() &&
      date.getUTCMonth() === monthStart.getUTCMonth();

    cells.push({
      key,
      date,
      dayNumber: date.getUTCDate(),
      inCurrentMonth,
      lines: linesByDay.get(key) || [],
    });
  }

  const weeks: CalendarDayCell[][] = [];
  for (let i = 0; i < weeksCount; i += 1) {
    weeks.push(cells.slice(i * 7, i * 7 + 7));
  }

  return {
    key: toMonthKey(monthStart),
    monthStart,
    weeks,
  };
}

function extractMetricValue(
  row: DataRecord,
  metricKey: string | undefined,
  dateKey: string,
  labelKey?: string,
): unknown {
  if (metricKey && row[metricKey] !== undefined) {
    return row[metricKey];
  }

  const fallbackKeys = Object.keys(row).filter(key => key !== dateKey && key !== labelKey);

  for (const key of fallbackKeys) {
    const value = row[key];
    if (typeof value === 'number') {
      return value;
    }
  }

  for (const key of fallbackKeys) {
    const value = row[key];
    if (value !== null && value !== undefined && value !== '' && !Number.isNaN(Number(value))) {
      return value;
    }
  }

  return undefined;
}

function readNumber(value: unknown, fallback: number): number {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
}

function readBoolean(value: unknown, fallback: boolean): boolean {
  if (typeof value === 'boolean') return value;
  if (value === 'true') return true;
  if (value === 'false') return false;
  return fallback;
}

function readString(value: unknown, fallback: string): string {
  return typeof value === 'string' && value.trim() !== '' ? value : fallback;
}

function readDisplayMode(value: unknown): CalendarDisplayMode {
  if (value === 'label_only' || value === 'label_value' || value === 'value_only') {
    return value;
  }
  return 'label_value';
}

function readOverflowMode(value: unknown): CalendarOverflowMode {
  if (value === 'scroll' || value === 'expand') {
    return value;
  }
  return 'scroll';
}

function readTextAlign(value: unknown, fallback: CalendarTextAlign): CalendarTextAlign {
  if (value === 'left' || value === 'center' || value === 'right') {
    return value;
  }
  return fallback;
}

function readVerticalAlign(
  value: unknown,
  fallback: CalendarVerticalAlign,
): CalendarVerticalAlign {
  if (value === 'top' || value === 'middle' || value === 'bottom') {
    return value;
  }
  return fallback;
}

export default function transformProps(
  chartProps: ChartProps,
): CalendarChartTransformedProps {
  const { width, height, queriesData, formData } = chartProps;
  const typedFormData = formData as CalendarChartFormData & Record<string, unknown>;
  const data = ((queriesData && queriesData[0] && queriesData[0].data) || []) as DataRecord[];

  const dimensions = (typedFormData.groupby || []).filter(Boolean);
  const metrics = (typedFormData.metrics || []).filter(Boolean);

  const dateKey = dimensions[0] || 'day_date';
  const labelKey = dimensions[1];
  const metricKey = metrics[0] ? getMetricLabel(metrics[0]) : undefined;

  const linesByDay = new Map<string, CalendarLine[]>();
  const monthMap = new Map<string, Date>();

  data.forEach(row => {
    const day = normalizeToUtcDate(row[dateKey]);
    if (!day) return;

    const dayKey = toDayKey(day);
    const monthKey = toMonthKey(day);
    const monthStart = monthStartUtc(day);

    if (!monthMap.has(monthKey)) {
      monthMap.set(monthKey, monthStart);
    }

    const line: CalendarLine = {
      label:
        labelKey && row[labelKey] !== null && row[labelKey] !== undefined
          ? String(row[labelKey])
          : undefined,
      value: extractMetricValue(row, metricKey, dateKey, labelKey),
      raw: row,
    };

    const arr = linesByDay.get(dayKey) || [];
    arr.push(line);
    linesByDay.set(dayKey, arr);
  });

  const months = Array.from(monthMap.values())
    .sort((a, b) => a.getTime() - b.getTime())
    .map(monthStart => buildMonthGrid(monthStart, linesByDay));

  return {
    width,
    height,
    months,

    monthsPerPage: Math.max(
      1,
      readNumber(typedFormData.months_per_page ?? typedFormData.monthsPerPage, 2),
    ),

    cellMinHeight: Math.max(
      70,
      readNumber(typedFormData.cell_min_height ?? typedFormData.cellMinHeight, 140),
    ),

    cellBodyMaxHeight: Math.max(
      60,
      readNumber(
        typedFormData.cell_body_max_height ?? typedFormData.cellBodyMaxHeight,
        180,
      ),
    ),

    showOutsideDays: readBoolean(
      typedFormData.show_outside_days ?? typedFormData.showOutsideDays,
      true,
    ),

    compactMode: readBoolean(
      typedFormData.compact_mode ?? typedFormData.compactMode,
      false,
    ),

    displayMode: readDisplayMode(
      typedFormData.display_mode ?? typedFormData.displayMode,
    ),

    overflowMode: readOverflowMode(
      typedFormData.overflow_mode ?? typedFormData.overflowMode,
    ),

    fontFamily: readString(
      typedFormData.font_family ?? typedFormData.fontFamily,
      'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Arial, sans-serif',
    ),

    monthTitleFontSize: Math.max(
      10,
      readNumber(
        typedFormData.month_title_font_size ?? typedFormData.monthTitleFontSize,
        18,
      ),
    ),

    headerFontSize: Math.max(
      10,
      readNumber(typedFormData.header_font_size ?? typedFormData.headerFontSize, 13),
    ),

    dayNumberFontSize: Math.max(
      10,
      readNumber(
        typedFormData.day_number_font_size ?? typedFormData.dayNumberFontSize,
        12,
      ),
    ),

    bodyFontSize: Math.max(
      10,
      readNumber(typedFormData.body_font_size ?? typedFormData.bodyFontSize, 12),
    ),

    bodyLineHeight: Math.max(
      1,
      readNumber(typedFormData.body_line_height ?? typedFormData.bodyLineHeight, 1.3),
    ),

    bodyTextAlign: readTextAlign(
      typedFormData.body_text_align ?? typedFormData.bodyTextAlign,
      'left',
    ),

    bodyVerticalAlign: readVerticalAlign(
      typedFormData.body_vertical_align ?? typedFormData.bodyVerticalAlign,
      'top',
    ),

    dayNumberAlign: readTextAlign(
      typedFormData.day_number_align ?? typedFormData.dayNumberAlign,
      'right',
    ),

    dateKey,
    labelKey,
    metricKey,
  };
}
